import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createArrondissementApi,
  createQuartierApi,
  deleteArrondissementApi,
  deleteQuartierApi,
  fetchArrondissementById,
  fetchArrondissements,
  fetchArrondissementsByCity,
  fetchCitiesByProvince,
  fetchCountries,
  fetchProvincesByCountry,
  fetchQuartierById,
  fetchQuartiers,
  updateArrondissementApi,
  updateQuartierApi,
} from "../services/geographyService";

const GEOGRAPHY_QK = ["geography"];
const GEOGRAPHY_STALE_TIME = 5 * 60 * 1000;

const baseQueryOptions = {
  staleTime: GEOGRAPHY_STALE_TIME,
  refetchOnWindowFocus: false,
};

export const geographyKeys = {
  all: GEOGRAPHY_QK,
  countries: [...GEOGRAPHY_QK, "countries"],
  provinces: (countryId) => [...GEOGRAPHY_QK, "provinces", countryId],
  cities: (provinceId) => [...GEOGRAPHY_QK, "cities", provinceId],
  arrondissementsByCity: (cityId) => [...GEOGRAPHY_QK, "arrondissementsByCity", cityId],
  arrondissements: [...GEOGRAPHY_QK, "arrondissements"],
  arrondissement: (id) => [...GEOGRAPHY_QK, "arrondissement", id],
  quartiers: [...GEOGRAPHY_QK, "quartiers"],
  quartier: (id) => [...GEOGRAPHY_QK, "quartier", id],
};

export const useCountries = (options = {}) =>
  useQuery({
    queryKey: geographyKeys.countries,
    queryFn: fetchCountries,
    ...baseQueryOptions,
    ...options,
  });

export const useProvinces = (countryId, options = {}) =>
  useQuery({
    queryKey: geographyKeys.provinces(countryId),
    queryFn: () => fetchProvincesByCountry(countryId),
    enabled: Boolean(countryId),
    ...baseQueryOptions,
    ...options,
  });

export const useCities = (provinceId, options = {}) =>
  useQuery({
    queryKey: geographyKeys.cities(provinceId),
    queryFn: () => fetchCitiesByProvince(provinceId),
    enabled: Boolean(provinceId),
    ...baseQueryOptions,
    ...options,
  });

export const useArrondissementsByCity = (cityId, options = {}) =>
  useQuery({
    queryKey: geographyKeys.arrondissementsByCity(cityId),
    queryFn: () => fetchArrondissementsByCity(cityId),
    enabled: Boolean(cityId),
    ...baseQueryOptions,
    ...options,
  });

export const useArrondissements = (options = {}) =>
  useQuery({
    queryKey: geographyKeys.arrondissements,
    queryFn: fetchArrondissements,
    ...baseQueryOptions,
    ...options,
  });

export const useArrondissement = (id, options = {}) =>
  useQuery({
    queryKey: geographyKeys.arrondissement(id),
    queryFn: () => fetchArrondissementById(id),
    enabled: Boolean(id),
    ...baseQueryOptions,
    ...options,
  });

export const useQuartiers = (options = {}) =>
  useQuery({
    queryKey: geographyKeys.quartiers,
    queryFn: fetchQuartiers,
    ...baseQueryOptions,
    ...options,
  });

export const useQuartier = (id, options = {}) =>
  useQuery({
    queryKey: geographyKeys.quartier(id),
    queryFn: () => fetchQuartierById(id),
    enabled: Boolean(id),
    ...baseQueryOptions,
    ...options,
  });

export const useCreateArrondissement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createArrondissementApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: geographyKeys.all });
      toast.success("Arrondissement ajouté avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Une erreur est survenue lors de l'ajout de l'arrondissement", {
        autoClose: 3000,
      });
    },
  });
};

export const useUpdateArrondissement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateArrondissementApi,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: geographyKeys.all });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: geographyKeys.arrondissement(variables.id),
        });
      }
      toast.success("Arrondissement modifié avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error(
        "Une erreur est survenue lors de la modification de l'arrondissement",
        { autoClose: 3000 }
      );
    },
  });
};

export const useDeleteArrondissement = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteArrondissementApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: geographyKeys.all });
      toast.success("Arrondissement supprimé avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error(
        "Une erreur est survenue lors de la suppression de l'arrondissement",
        { autoClose: 3000 }
      );
    },
  });
};

export const useCreateQuartier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createQuartierApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: geographyKeys.all });
      toast.success("Quartier ajouté avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Une erreur est survenue lors de l'ajout du quartier", {
        autoClose: 3000,
      });
    },
  });
};

export const useUpdateQuartier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateQuartierApi,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: geographyKeys.all });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: geographyKeys.quartier(variables.id),
        });
      }
      toast.success("Quartier modifié avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Une erreur est survenue lors de la modification du quartier", {
        autoClose: 3000,
      });
    },
  });
};

export const useDeleteQuartier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteQuartierApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: geographyKeys.all });
      toast.success("Quartier supprimé avec succès", { autoClose: 3000 });
    },
    onError: () => {
      toast.error("Une erreur est survenue lors de la suppression du quartier", {
        autoClose: 3000,
      });
    },
  });
};
